/**
 * The AI Service detail page's two schema cards (task 785).
 *
 * The Request (Input) and Response (Output) schemas are the service's CONTRACT
 * — what a caller must send and what they get back. They used to sit read-only
 * at the bottom of Overview, which made Overview a long page and left the
 * schemas uneditable anywhere in this hub. Task 785 gave them an Input/Output
 * tab of their own, where each card gained its own read → edit → save cycle.
 *
 * Task 882 dropped that tab and returned the pair to Overview, below the Request
 * card, as `ServiceSchemaCards` — the editable cycle is what task 785 was for,
 * and it travels with them, so this is the move back WITHOUT the read-only
 * regression. The file keeps its name because the cards are all it ever held;
 * `?tab=input-output` is now a permanent alias onto Overview.
 *
 * Two properties this deliberately keeps:
 *
 *   1. **Read-only is the default.** A card renders the stored schema until the
 *      User presses Edit, and the "View as JSON" toggle works in that state
 *      exactly as it did on Overview — reading a schema must never require
 *      entering an editing mode that could be left half-finished.
 *   2. **The editing surface is the one the create form already uses.**
 *      `SchemaBuilder` — the same visual field list, the same Edit-as-JSON
 *      escape hatch, the same field editor modal — rather than a second schema
 *      editor that could drift from `EndpointForm`'s.
 *
 * Each card saves ONLY its own field, through the shared endpoint update hook,
 * so editing the response schema can never clobber a concurrent change to the
 * request schema. Failures resolve through `describeEndpointSaveError`, the same
 * classifier the create form and the inline path editor use.
 */
import { useState } from 'react';
import { CheckIcon, DocumentTextIcon, PencilIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type { Endpoint, UpdateEndpointInput } from '@/types';
import { useUpdateEndpoint } from '@/hooks/useEndpoints';
import { useToast } from '@/contexts/ToastContext';
import { SchemaBuilder } from '@/components/schema-builder/SchemaBuilder';
import { describeEndpointSaveError } from '@/components/endpoints/endpointSaveError';

/** The two schema fields an AI Service declares. */
type SchemaField = 'input' | 'output';

interface SchemaCardProps {
  endpoint: Endpoint;
  field: SchemaField;
  title: string;
  /** Headline for the card when no schema is defined yet. */
  emptyHeadline: string;
  /** What defining a schema would buy the User, shown under the headline. */
  emptyHelp: string;
}

function SchemaCard({ endpoint, field, title, emptyHeadline, emptyHelp }: SchemaCardProps) {
  const stored = endpoint[field];
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string | undefined>();
  const updateEndpoint = useUpdateEndpoint();
  const toast = useToast();

  const startEdit = () => {
    setDraft(stored);
    setError(undefined);
    setIsEditing(true);
  };

  /**
   * Cancel discards the draft rather than writing anything back: the read-only
   * view renders `endpoint[field]` — the server's copy — so dropping the draft
   * IS the restore.
   */
  const cancelEdit = () => {
    setIsEditing(false);
    setDraft(null);
    setError(undefined);
  };

  const save = async () => {
    try {
      await updateEndpoint.mutateAsync({
        id: endpoint.id,
        /**
         * `UpdateEndpointInput` is `Partial<CreateEndpointInput>`, where an
         * absent schema means "this service declares none". An UPDATE has to be
         * able to say something the create shape cannot: "clear the schema I
         * used to have" — which is what emptying the field list produces
         * (`fieldsToJsonSchema([])` is null). Sending `undefined` there would be
         * a save that silently does nothing, the exact failure mode task 388
         * removed from this page, so the null is sent and the cast is confined
         * to this one call.
         */
        data: { [field]: draft } as unknown as UpdateEndpointInput,
      });
      setIsEditing(false);
      setDraft(null);
      setError(undefined);
      toast.success(`${title} saved`);
    } catch (err) {
      const display = describeEndpointSaveError(err, `Failed to save ${title}`);
      setError(display.message);
      toast.error(display.message);
    }
  };

  const shown = isEditing ? draft : stored;

  return (
    <div
      data-testid={`schema-card-${field}`}
      className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6"
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <h2 className="text-lg font-medium text-gray-900 dark:text-white">{title}</h2>
        <div className="flex shrink-0 items-center gap-2">
          {isEditing ? (
            <>
              <button
                type="button"
                onClick={save}
                disabled={updateEndpoint.isPending}
                data-testid={`schema-save-${field}`}
                className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700 disabled:opacity-50"
              >
                <CheckIcon className="-ml-0.5 mr-1 h-4 w-4" />
                {updateEndpoint.isPending ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                onClick={cancelEdit}
                disabled={updateEndpoint.isPending}
                data-testid={`schema-cancel-${field}`}
                className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                <XMarkIcon className="-ml-0.5 mr-1 h-4 w-4" />
                Cancel
              </button>
            </>
          ) : (
            /* Offered on an empty card too — defining the contract for the
               first time is the same action as changing it, and without this
               there is no way to add a schema from the hub at all. */
            <button
              type="button"
              onClick={startEdit}
              data-testid={`schema-edit-${field}`}
              className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <PencilIcon className="-ml-0.5 mr-1 h-4 w-4" />
              Edit
            </button>
          )}
        </div>
      </div>

      {shown || isEditing ? (
        /* The title is the card's own heading above, so the builder's label is
           empty here — its header row still carries the JSON toggle and (in
           edit mode) Add Field, which is what keeps "View as JSON" working
           exactly as it did on Overview. */
        <SchemaBuilder
          label=""
          value={shown}
          onChange={setDraft}
          readOnly={!isEditing}
          error={error}
        />
      ) : (
        <div className="text-center py-6">
          <DocumentTextIcon className="h-8 w-8 text-gray-400 dark:text-gray-500 mx-auto mb-2" />
          <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">{emptyHeadline}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">{emptyHelp}</p>
          {error && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>}
        </div>
      )}
    </div>
  );
}

interface ServiceSchemaCardsProps {
  endpoint: Endpoint;
}

/**
 * The pair, side by side on wide screens. Overview renders this; nothing else
 * wraps it, which is why the tab-level component it replaced is gone.
 */
export function ServiceSchemaCards({ endpoint }: ServiceSchemaCardsProps) {
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <SchemaCard
        endpoint={endpoint}
        field="input"
        title="Request (Input)"
        emptyHeadline="No input schema defined"
        emptyHelp="This endpoint accepts any JSON input. Define a schema to validate requests."
      />
      <SchemaCard
        endpoint={endpoint}
        field="output"
        title="Response (Output)"
        emptyHeadline="No output schema defined"
        emptyHelp="This endpoint returns any JSON output. Define a schema to document the response structure."
      />
    </div>
  );
}

export default ServiceSchemaCards;
